import { debounceTime, take, filter, delayWhen } from 'rxjs/operators';
import { UsersService } from '@shared/services/users/users.service';
import { Platform } from '@angular/cdk/platform';
import { Subject, Observable, from, map, switchMap } from 'rxjs';
import { createNgModule, Injectable, ApplicationRef, Optional, ViewContainerRef, EmbeddedViewRef } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { UserInteractionService } from '../user-interaction/user-interaction.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import type { SnackbarTemplatesComponent } from './snackbar-templates/snackbar-templates.component';
import { BlockUiService } from 'app/shared/components/block-ui/block-ui.service';

export type NotificationMessageType = 'info' | 'success' | 'warning' | 'error' | 'loading';
export type NotificationMessage = {
  title: string;
  text: string;
}

export type NotificationArgs = {
  message: NotificationMessage,
  messageType: NotificationMessageType,
  cb?: () => void,
  duration?: number,
  verticalPosition?: MatSnackBarVerticalPosition
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationSubject: Subject<NotificationArgs> = new Subject();
  private snackBar: MatSnackBar;
  private vc: ViewContainerRef;


  get messageTemplates(): Promise<SnackbarTemplatesComponent> {
    return ( async () => {
      if (!this._messageTemplates) {
        const snackbar = await import('./snackbar-templates/snackbar-templates.component').then(c => c.SnackbarTemplatesComponent);
        this._messageTemplates = this.vc.createComponent(snackbar).instance;
      }
      return this._messageTemplates;
    })();

  }
  private _messageTemplates: SnackbarTemplatesComponent;

  constructor(
    public usersService: UsersService,
    private platform: Platform,
    private appRef: ApplicationRef,
    private userInteractionService: UserInteractionService,
    @Optional() private swUpdate: SwUpdate,
    private blockUiService: BlockUiService
  ) {
    if (this.platform.isBrowser) {

      this.userInteractionService.onUserAction$
      .subscribe(() => {
        this.initSnackBar().subscribe(() => {
          if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates
            .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
            .subscribe(() => {
              this.showWarningMessage($localize`New Application version is available. Reloading...`, null, () => {
                document.location.reload();
              });
            });
          }
        });
      });

      this.notificationSubject
      .pipe(
        delayWhen(() => this.initSnackBar()),
        debounceTime(500),
      )
      .subscribe(async ({message, messageType, cb, duration, verticalPosition}) => {
        duration = duration > 0 ? duration : null;
        const tpls = await this.messageTemplates;
        const { snackBar } = this;
        let sb: MatSnackBarRef<EmbeddedViewRef<any>>;
        snackBar.dismiss();
        tpls.message = message;
        let tpl;
        switch (messageType) {
          case 'info':
            tpl = tpls.infoTpl;
          break;
          case 'success':
            tpl = tpls.successTpl;
          break;
          case 'warning':
            tpl = tpls.warningTpl;
          break;
          case 'error':
            tpl = tpls.errorTpl;
          break;
          case 'loading':
            tpl = tpls.loadingTpl;
          break;
        }
        this.blockUiService?.hide();
        sb = snackBar.openFromTemplate(tpl, { duration, verticalPosition});
        if (cb) {
          sb.afterDismissed().subscribe(() => cb());
        }
      });
    }
  }

  initSnackBar(): Observable<boolean> {
    return from(import('@angular/material/snack-bar'))
    .pipe(
      map(({MatSnackBarModule}) => {
        return createNgModule(MatSnackBarModule, this.appRef.injector);
      }),
      switchMap(moduleRef => {
        return from(import('@angular/material/snack-bar'))
          .pipe(
            map(({MatSnackBar}) => {
              this.snackBar = moduleRef.injector.get<MatSnackBar>(MatSnackBar);
              return true;
            })
          )
      }),
      take(1)
    );
  }

  setViewContainerRef(vc: ViewContainerRef): void {
    this.vc = vc;
  }

  showInfoMessage(text: string, title = $localize`Info`, cb?: () => void, duration?: number, verticalPosition?: MatSnackBarVerticalPosition): void {
    this.showMessage({text, title},'info', cb, duration, verticalPosition);
  }

  showSuccessMessage(text: string, title = $localize`Success`, cb?: () => void, duration?: number, verticalPosition?: MatSnackBarVerticalPosition): void {
    this.showMessage({text, title},'success', cb, duration, verticalPosition);
  }

  showWarningMessage(text: string, title = $localize`Warning`, cb?: () => void, duration: number = 6000, verticalPosition: MatSnackBarVerticalPosition = 'top'): void {
    this.showMessage({text, title},'warning', cb, duration, verticalPosition);
  }

  showErrorMessage(text: string, title = $localize`Error`, cb?: () => void, duration: number = 6000, verticalPosition: MatSnackBarVerticalPosition = 'top'): void {
    this.showMessage({text, title},'error', cb, duration, verticalPosition);
  }

  showLoadingMessage(text: string, title = $localize`Waiting...`, cb?: () => void, duration?: number, verticalPosition?: MatSnackBarVerticalPosition): void {
    this.showMessage({text, title},'loading', cb, duration, verticalPosition);
  }

  private showMessage(message: NotificationMessage, messageType: NotificationMessageType, cb?: () => void, duration = 4000, verticalPosition: MatSnackBarVerticalPosition = 'bottom'): void {
    this.hideMessage();
    this.notificationSubject.next({message, messageType, cb, duration, verticalPosition});
  }

  hideMessage(): void {
    if (this.snackBar) {
      this.snackBar.dismiss();
    }
  }
}
